import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useModal from 'hooks/useModal';
import { ModalNameConstant } from 'constants/modal.constant';
import ConsultingAssignRepository from 'repositories/ConsultingAssignRepository';
import { CommonConstant, ReservationStatusNameConstant } from 'constants/code.constant';
import CommonUtil from 'utils/common.util';
import { ErrorCodeConstant } from 'constants/errorCode.constant';
import useConsultingAssign from 'hooks/useConsultingAssign';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import TableSortHeader from 'components/table/TableSortHeader';

const SaveForm = ({ projectId, projectItemId, initData, refetch }) => {
  const { openModal } = useModal();

  const defaultValues = {
    projectItemId: projectItemId,
    consultantId: '',
    consultantName: '',
    consultingName: '',
    requiredScheduleCount: '',
    assignedClientIds: [],
  };

  let schemeObject = {
    consultantId: yup.string().trim().required('컨설턴트를 선택해 주세요'),
    consultingName: yup.string().trim().required('컨설팅명을 입력해 주세요'),
    requiredScheduleCount: yup
      .string()
      .trim()
      .required('컨설턴트가 수행해야 하는 일정 횟수 입력해 주세요')
      .max(18, '최대 10자리까지 입력 가능합니다'),
  };

  const schema = yup.object().shape(schemeObject);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ defaultValues, mode: 'onBlur', resolver: yupResolver(schema) });

  const openConsultantSelectModal = () => {
    openModal(ModalNameConstant.PROJECT_ITEM_CONSULTANT_SELECT, {
      consultantSelectCallback,
      selectedConsultantId: getValues('consultantId'),
    });
  };

  const consultantSelectCallback = ({ accountId = '', name = '' }) => {
    setValue('consultantId', accountId, { shouldValidate: true });
    setValue('consultantName', name);
  };

  const openClientSelectModal = ({ showSelectedOnly = false }) => {
    openModal(ModalNameConstant.PROJECT_ITEM_CLIENT_SELECT, {
      projectId,
      selectedClientIds: getValues('assignedClientIds'),
      clientSelectCallback,
      showSelectedOnly,
    });
  };

  const clientSelectCallback = (accountIds = []) => {
    setValue('assignedClientIds', accountIds, { shouldValidate: true });
  };

  const saveConsultantAssign = (formData) => {
    if (!window.confirm('저장하시겠습니까?')) {
      return;
    }

    ConsultingAssignRepository.saveConsultingAssign(formData)
      .then((res) => {
        if (res.result === CommonConstant.OK) {
          alert('저장하였습니다');
          refetch();

          if (!initData) {
            reset();
          }
        }

        if (res.errorCode === ErrorCodeConstant.ERROR_CODE.EXCEEDED_REQUIRED_SCHEDULE_COUNT) {
          alert('전체 일정 횟수를 초과합니다');
        }
      })
      .catch(CommonUtil.repoErrFunc);
  };

  const removeConsultantAssign = () => {
    if (!window.confirm('삭제하시겠습니까?')) {
      return;
    }

    ConsultingAssignRepository.removeConsultingAssign(initData.consultantAssignId)
      .then((res) => {
        if (res.result === CommonConstant.OK) {
          alert('삭제하였습니다');
          refetch();
        } else {
          console.error(res);
          alert('알 수 없는 에러 코드');
        }
      })
      .catch(CommonUtil.repoErrFunc);
  };

  useEffect(() => {
    if (initData) {
      const { reservations, ...rest } = initData;
      reset({ ...rest, projectItemId });
    }
  }, [initData]);

  return (
    <form noValidate autoComplete="off" onSubmit={handleSubmit(saveConsultantAssign)}>
      <div className="page-card">
        <div className="table-search-wrap detail-form-wrap">
          <div>
            <label>컨설턴트 선택 {!initData && '(최초 등록 이후 수정 하실 수 없습니다)'}</label>
            <input
              type="text"
              value={getValues('consultantName')}
              placeholder="클릭하여 선택"
              readOnly
              onClick={openConsultantSelectModal}
              disabled={initData}
            />
            {errors.consultantId && <p className="error">{errors.consultantId.message}</p>}
          </div>
          <Controller
            name={'consultingName'}
            control={control}
            rules={{ required: true }}
            render={({ field: { onBlur, value, onChange } }) => (
              <div>
                <label>컨설팅명</label>
                <input type="text" placeholder="컨설팅명" onBlur={onBlur} value={value} onChange={onChange} />
                {errors.consultingName && <p className="error">{errors.consultingName.message}</p>}
              </div>
            )}
          />
          <div>
            <label>참여 기업 선택</label>
            <div className="company-selection">
              <input
                type="text"
                placeholder="클릭하여 선택"
                value={
                  getValues('assignedClientIds').length > 0
                    ? `선택된 기업 개수: ${getValues('assignedClientIds').length}`
                    : ''
                }
                readOnly
                onClick={openClientSelectModal}
              />
              {initData && getValues('assignedClientIds').length > 0 && (
                <button
                  className="btn edit-btn"
                  type="button"
                  onClick={() => openClientSelectModal({ showSelectedOnly: true })}
                >
                  보기
                </button>
              )}
            </div>
          </div>
          <Controller
            name={'requiredScheduleCount'}
            control={control}
            rules={{ required: true }}
            render={({ field: { onBlur, value, onChange } }) => (
              <div>
                <label>컨설턴트가 수행해야 하는 일정 횟수</label>
                <input type="number" placeholder="일정 횟수" onBlur={onBlur} value={value} onChange={onChange} />
                {errors.requiredScheduleCount && <p className="error">{errors.requiredScheduleCount.message}</p>}
              </div>
            )}
          />
          {initData && (
            <>
              <div />
              <div>
                <label>등록일시</label>
                <input type="text" value={format(initData.createdAt, 'yyyy-MM-dd HH:mm:ss')} disabled />
              </div>
            </>
          )}
          {initData && (
            <div
              style={{
                gridColumn: '1 / -1',
              }}
            >
              <h3>예약 현황</h3>
              <div className="table-wrap">
                <table>
                  <TableSortHeader
                    headCells={[
                      { label: '#', width: '5%' },
                      { label: '기업명', width: '20%' },
                      { label: '일자', width: '10%' },
                      { label: '일시', width: '20%' },
                      { label: '총 시간', width: '10%' },
                      { label: '현황', width: '10%' },
                    ]}
                  />
                  <tbody>
                    {initData.reservations.length === 0 && (
                      <tr>
                        <td colSpan={6}>예약 현황이 없습니다.</td>
                      </tr>
                    )}
                    {initData.reservations.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.company}</td>
                        <td>{format(new Date(item.date), 'yyyy-MM-dd (EEEE)', { locale: ko })}</td>
                        <td>{item.time}</td>
                        <td>{item.totalTime}</td>
                        <td>{ReservationStatusNameConstant[item.status]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      <br />
      <div className="display-flex space-between">
        <div />
        <div>
          {initData && (
            <>
              <button className="btn error-btn" type="button" onClick={removeConsultantAssign}>
                삭제
              </button>
              &nbsp;&nbsp;
            </>
          )}
          <button className="btn add-btn" type="submit">
            저장
          </button>
        </div>
      </div>
    </form>
  );
};

const ProjectItemDetailConsultingForm = React.memo(({ projectId, projectItemId }) => {
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);

  const { useGetConsultingAssignList } = useConsultingAssign();

  const { list: consultingAssignList, totalCount, refetch } = useGetConsultingAssignList({ projectItemId });

  const saveFormProps = {
    projectId,
    projectItemId,
    refetch,
  };

  const handleClickOutside = (e) => {
    if (e.target.className === 'drawer-overlay') {
      setIsOpenDrawer(false);
    }
  };

  useEffect(() => {
    if (isOpenDrawer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpenDrawer]);

  return (
    <>
      <br />
      <button className="btn add-btn" type="button" onClick={() => setIsOpenDrawer(true)}>
        컨설팅 등록
      </button>
      {totalCount > 0 && (
        <>
          <br />
          <br />
          <div className="page-header">컨설팅 목록 {totalCount}개</div>
          {consultingAssignList.map((item, index) => (
            <div key={index}>
              <SaveForm {...saveFormProps} initData={item} />
              <br />
            </div>
          ))}
        </>
      )}
      {isOpenDrawer && (
        <div className="drawer-overlay" onClick={handleClickOutside}>
          <div className="drawer-content">
            <div className="close" onClick={() => setIsOpenDrawer(false)}>
              &times;
            </div>
            <div className="page-header">컨설팅 등록</div>
            <SaveForm {...saveFormProps} />
          </div>
        </div>
      )}
    </>
  );
});

export default ProjectItemDetailConsultingForm;
